import { Component, HostListener, Inject } from '@angular/core';
import { HygieneDayWiseVendor, HygineDayWiseService } from '../hygine-day-wise.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';


export type HdwCollegeType = 'SCHOOL' | 'COLLEGE';
export type HdwBoardingType = 'RESIDENTIAL' | 'DAY_SCHOLAR';


export interface HygieneDayWiseFilterDialogData {
  selectedVendorId: string | null;
  selectedVendorName?: string | null;
  selectedDepartmentName?: string | null;
  collegeTypes?: HdwCollegeType[];
  boardingTypes?: HdwBoardingType[];
}

export interface HygieneDayWiseFilterDialogResult {
  selectedVendorId: string | null;
  selectedVendorName: string | null;
  collegeTypes: HdwCollegeType[];
  boardingTypes: HdwBoardingType[];
}

@Component({
  selector: 'app-hygine-day-wise-filterdialog',
  templateUrl: './hygine-day-wise-filterdialog.component.html',
  styleUrls: ['./hygine-day-wise-filterdialog.component.scss']
})
export class HygineDayWiseFilterdialogComponent {
 selectedVendorId: string | null = null;
  selectedVendorDisplayName: string | null = null;
  collegeTypes: HdwCollegeType[] = ['SCHOOL', 'COLLEGE'];
  boardingTypes: HdwBoardingType[] = ['RESIDENTIAL', 'DAY_SCHOLAR'];

  vendors: HygieneDayWiseVendor[] = [];
  filteredVendorList: HygieneDayWiseVendor[] = [];
  vendorQuery = '';
  vendorOpen = false;
  vendorsLoading = true;
  vendorsError = '';
  selectedDepartmentName = 'HOUSEKEEPING';
  segmentExpanded = true;
  campusTypeExpanded = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: HygieneDayWiseFilterDialogData,
    private dialogRef: MatDialogRef<HygieneDayWiseFilterDialogData>,
    private dayWise:HygineDayWiseService
  ) {
    this.selectedVendorId =
      data?.selectedVendorId != null && String(data.selectedVendorId).trim() !== ''
        ? String(data.selectedVendorId).trim()
        : null;
    this.selectedVendorDisplayName =
      data?.selectedVendorName != null && String(data.selectedVendorName).trim() !== ''
        ? String(data.selectedVendorName).trim()
        : null;
    if (data?.collegeTypes != null) {
      this.collegeTypes = [...data.collegeTypes];
    }
    if (data?.boardingTypes != null) {
      this.boardingTypes = [...data.boardingTypes];
    }
    const dept = (data?.selectedDepartmentName ?? '').toString().trim();
    if (dept) this.selectedDepartmentName = dept;
    /** Do not call `applyOrgSelectionForVendor()` here: reopen must keep segment/campus from parent (API state). “All selected” runs only when user picks/clears vendor in-dialog. */
  }
  private applyOrgSelectionForVendor(): void {
    this.collegeTypes = ['SCHOOL', 'COLLEGE'];
    this.boardingTypes = ['RESIDENTIAL', 'DAY_SCHOLAR'];
  }

  get vendorTriggerText(): string {
    if (this.selectedVendorId == null || String(this.selectedVendorId).trim() === '') {
      return 'All vendors';
    }
    const v = this.vendors.find((x) => String(x.vendorId) === String(this.selectedVendorId));
    if (v?.vendorName?.trim()) return v.vendorName.trim();
    if (this.vendorsLoading && this.selectedVendorDisplayName?.trim()) {
      return this.selectedVendorDisplayName.trim();
    }
    return 'All vendors';
  }

  isVendorOptionSelected(v: HygieneDayWiseVendor): boolean {
    if (this.selectedVendorId == null || String(this.selectedVendorId).trim() === '') return false;
    return String(v.vendorId) === String(this.selectedVendorId);
  }

  ngOnInit(): void {
    this.loadVendors();
  }
  private syncVendorSelectionWithLoadedList(): void {
    if (this.selectedVendorId == null || String(this.selectedVendorId).trim() === '') {
      return;
    }
    const id = String(this.selectedVendorId).trim();
    const hit = this.vendors.find((x) => String(x.vendorId) === id);
    if (!hit) {
      this.selectedVendorId = null;
      this.selectedVendorDisplayName = null;
      this.applyOrgSelectionForVendor();
      return;
    }
    this.selectedVendorDisplayName = (hit.vendorName ?? '').trim() || null;
  }

  private loadVendors(): void {
    this.vendorsLoading = true;
    this.vendorsError = '';
    this.dayWise.getVendorList({ expHead: this.selectedDepartmentName }).subscribe({
      next: (list) => {
        this.vendors = list;
        this.filteredVendorList = [...list];
        this.syncVendorSelectionWithLoadedList();
        this.vendorsLoading = false;
      },
      error: () => {
        this.vendors = [];
        this.filteredVendorList = [];
        this.syncVendorSelectionWithLoadedList();
        this.vendorsError = 'Could not load vendors.';
        this.vendorsLoading = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target.closest('.hdwf-vendor-combo')) {
      this.vendorOpen = false;
    }
  }

  openVendor(): void {
    if (this.vendorsLoading) return;
    if (this.vendorOpen) {
      this.vendorOpen = false;
      return;
    }
    this.vendorOpen = true;
    this.vendorQuery = '';
    this.filterVendors();
  }

  filterVendors(): void {
    const q = (this.vendorQuery || '').toLowerCase().trim();
    if (!q) {
      this.filteredVendorList = [...this.vendors];
      return;
    }
    this.filteredVendorList = this.vendors.filter((v) =>
      (v.vendorName || '').toLowerCase().includes(q)
    );
  }

  selectVendorClear(): void {
    this.selectedVendorId = null;
    this.selectedVendorDisplayName = null;
    this.vendorOpen = false;
    this.applyOrgSelectionForVendor();
  }

  selectVendor(v: HygieneDayWiseVendor): void {
    const id = String(v.vendorId ?? '').trim();
    this.selectedVendorId = id.length > 0 ? id : null;
    this.selectedVendorDisplayName = (v.vendorName ?? '').trim() || null;
    this.vendorOpen = false;
    this.applyOrgSelectionForVendor();
  }

  toggleCollegeType(mode: HdwCollegeType): void {
    if (this.collegeTypes.includes(mode)) {
      this.collegeTypes = this.collegeTypes.filter((x) => x !== mode);
    } else {
      this.collegeTypes = [...this.collegeTypes, mode];
    }
  }

  toggleSegmentSection(): void {
    this.segmentExpanded = !this.segmentExpanded;
  }

  get segmentAllChecked(): boolean {
    return this.collegeTypes.includes('SCHOOL') && this.collegeTypes.includes('COLLEGE');
  }

  get segmentAllIndeterminate(): boolean {
    const s = this.collegeTypes.includes('SCHOOL');
    const c = this.collegeTypes.includes('COLLEGE');
    return (s || c) && !this.segmentAllChecked;
  }

  /** All = both School and College; uncheck All clears both */
  toggleSegmentAll(): void {
    if (this.segmentAllChecked) {
      this.collegeTypes = this.collegeTypes.filter((x) => x !== 'SCHOOL' && x !== 'COLLEGE');
    } else {
      const set = new Set<HdwCollegeType>([...this.collegeTypes, 'SCHOOL', 'COLLEGE']);
      this.collegeTypes = [...set];
    }
  }

  toggleCampusTypeSection(): void {
    this.campusTypeExpanded = !this.campusTypeExpanded;
  }

  get campusTypeAllChecked(): boolean {
    return this.boardingTypes.includes('RESIDENTIAL') && this.boardingTypes.includes('DAY_SCHOLAR');
  }

  get campusTypeAllIndeterminate(): boolean {
    const r = this.boardingTypes.includes('RESIDENTIAL');
    const d = this.boardingTypes.includes('DAY_SCHOLAR');
    return (r || d) && !this.campusTypeAllChecked;
  }
  toggleCampusTypeAll(): void {
    if (this.campusTypeAllChecked) {
      this.boardingTypes = this.boardingTypes.filter((x) => x !== 'RESIDENTIAL' && x !== 'DAY_SCHOLAR');
    } else {
      const set = new Set<HdwBoardingType>([...this.boardingTypes, 'RESIDENTIAL', 'DAY_SCHOLAR']);
      this.boardingTypes = [...set];
    }
  }

  toggleBoarding(mode: HdwBoardingType): void {
    if (this.boardingTypes.includes(mode)) {
      this.boardingTypes = this.boardingTypes.filter((x) => x !== mode);
    } else {
      this.boardingTypes = [...this.boardingTypes, mode];
    }
  }

  apply(): void {
    let selectedVendorName: string | null = null;
    if (this.selectedVendorId != null && String(this.selectedVendorId).trim() !== '') {
      const hit = this.vendors.find((x) => String(x.vendorId) === String(this.selectedVendorId));
      selectedVendorName = hit?.vendorName?.trim() || null;
    }
    const result: HygieneDayWiseFilterDialogResult = {
      selectedVendorId: this.selectedVendorId,
      selectedVendorName,
      collegeTypes: [...this.collegeTypes],
      boardingTypes: [...this.boardingTypes]
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
